import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Dropdown,
  DropdownButton,
  Form,
  Modal,
  Spinner,
  Table,
} from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { CountrySelect } from '../common/CountrySelect';
import DropdownSelect from '../common/DropdownSelect';
import { supabase } from '../../lib/supabase';
import { captureSupabaseError } from '../../lib/sentry';
import type { SupplyChainSupplier } from '../../hooks/useSupplyChain';
import { getCountryNameFromIso } from '../../util/countryUtil';
import type {
  DropdownSelectOption,
  SupplyChainSupplierDocument,
  UserOrganisation,
} from '../../types/types';
import {
  getSupplierAnalysisProblems,
  isDocumentExpired,
} from '../../util/supplyChainAnalysis';

const MAX_FILES = 5;
const riskOptions = ['Low', 'Medium', 'High'] as const;
const riskBadgeVariantByLevel: Record<string, string> = {
  low: 'success',
  medium: 'warning',
  high: 'danger',
};

const emptyUploadForm = {
  fileName: '',
  validUntil: '',
};

const emptyRequestForm = {
  email: '',
  message: '',
  dueDate: '',
  requestedDocumentName: '',
  items: [] as SupplyChainDocumentRequestItem[],
};

type RiskHelpTopic = 'country' | 'industry' | null;

type CountryRiskSuggestion = {
  country_code: string;
  risk_level: string;
  cpi_score: number | null;
  ituc_rating: number | null;
  suggestion_logic: string | null;
};

type IndustryRiskSuggestion = {
  industry_id: string;
  risk_level: string;
  risk_drivers: string | null;
};

type SupplyChainDocumentRequestItem = {
  id?: string;
  requested_document_name: string;
};

type SupplyChainDocumentRequest = {
  id: string;
  supplier_id: string;
  organisation_id: string;
  recipient_email: string | null;
  message: string | null;
  due_date: string | null;
  items: SupplyChainDocumentRequestItem[] | null;
  status: 'pending' | 'completed' | 'expired';
  created_at: string | null;
  deleted_at: string | null;
};

const riskHelpContent = {
  country: {
    heading: 'Geopolitical & Regulatory Landscape',
    body: (
      <>
        Assess risk based on the supplier&apos;s operating location. High-risk
        countries are characterized by weak labor laws, lack of freedom of
        association, or high levels of corruption. Refer to the{' '}
        <a
          href="https://www.ituc-csi.org/global-rights-index"
          target="_blank"
          rel="noreferrer"
        >
          ITUC Global Rights Index
        </a>{' '}
        (for labor) and the{' '}
        <a
          href="https://www.transparency.org/en/cpi/2025"
          target="_blank"
          rel="noreferrer"
        >
          Transparency International CPI
        </a>{' '}
        (for governance). Under ESG standards, operating in conflict-affected or
        high-risk areas requires enhanced due diligence to prevent complicity in
        human rights abuses.
      </>
    ),
  },
  industry: {
    heading: 'Sector-Specific Impact & Vulnerability',
    body: "Assess risk based on the nature of the supplier's business. Certain industries are inherently prone to specific ESG issues:",
    bullets: [
      'Manufacturing/Garment: High risk for forced labor and unsafe working conditions.',
      'Mining/Agriculture: High risk for child labor and environmental degradation (deforestation, water pollution).',
      'Chemicals/Heavy Industry: High risk for hazardous waste and carbon emissions.',
      'Evaluate whether the industry lacks collective bargaining or has a history of environmental non-compliance.',
    ],
  },
} as const;

const calculateComplianceStatus = (
  documents: SupplyChainSupplierDocument[],
  countryRisk: string | null,
  industryRisk: string | null,
) => {
  if (countryRisk === 'High' || industryRisk === 'High') {
    return 'Flagged';
  }

  if (documents.length === 0) {
    return 'Unverified';
  }

  if (documents.some((document) => isDocumentExpired(document))) {
    return 'Expired';
  }

  if (countryRisk === 'Low' && industryRisk === 'Low') {
    return 'Verified';
  }

  return 'Warning';
};

export function SupplierComplianceDocuments({
  supplierId,
  supplier,
  onSupplierUpdated,
}: {
  supplierId: string;
  supplier: SupplyChainSupplier;
  onSupplierUpdated: () => Promise<void>;
}) {
  const selectedOrganisation = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.organisation.selected,
  ) as UserOrganisation | undefined;

  const organisationId = selectedOrganisation?.organisation?.id ?? null;
  const supplierOrganisationId = supplier.organisation_id ?? organisationId;
  const bucketName = useMemo(() => 'supply-chain-compliance-documents', []);

  const [documents, setDocuments] = useState<SupplyChainSupplierDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [uploadForm, setUploadForm] = useState(emptyUploadForm);
  const [countryRisk, setCountryRisk] = useState(supplier.country_risk ?? '');
  const [industryRisk, setIndustryRisk] = useState(
    supplier.industry_risk ?? '',
  );
  const [riskHelpTopic, setRiskHelpTopic] = useState<RiskHelpTopic>(null);
  const [savingRiskField, setSavingRiskField] = useState<
    'country_risk' | 'industry_risk' | null
  >(null);
  const [selectedRiskHelpCountry, setSelectedRiskHelpCountry] = useState('');
  const [countryRiskSuggestion, setCountryRiskSuggestion] =
    useState<CountryRiskSuggestion | null>(null);
  const [countryRiskSuggestionLoading, setCountryRiskSuggestionLoading] =
    useState(false);
  const [countryRiskSuggestionError, setCountryRiskSuggestionError] = useState<
    string | null
  >(null);
  const [industryOptions, setIndustryOptions] = useState<
    DropdownSelectOption[]
  >([]);
  const [industryOptionsLoading, setIndustryOptionsLoading] = useState(false);
  const [selectedRiskHelpIndustry, setSelectedRiskHelpIndustry] = useState('');
  const [industryRiskSuggestion, setIndustryRiskSuggestion] =
    useState<IndustryRiskSuggestion | null>(null);
  const [industryRiskSuggestionLoading, setIndustryRiskSuggestionLoading] =
    useState(false);
  const [industryRiskSuggestionError, setIndustryRiskSuggestionError] =
    useState<string | null>(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestForm, setRequestForm] = useState({
    ...emptyRequestForm,
    email: supplier.email ?? '',
  });
  const [requestFormErrors, setRequestFormErrors] = useState<
    Record<string, string>
  >({});
  const [requestError, setRequestError] = useState<string | null>(null);
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestCancelling, setRequestCancelling] = useState(false);
  const [requestStep, setRequestStep] = useState<1 | 2>(1);
  const [existingDocumentRequest, setExistingDocumentRequest] =
    useState<SupplyChainDocumentRequest | null>(null);

  const availableSlots = Math.max(MAX_FILES - documents.length, 0);
  const currentComplianceStatus = useMemo(
    () =>
      calculateComplianceStatus(
        documents,
        countryRisk || null,
        industryRisk || null,
      ),
    [countryRisk, documents, industryRisk],
  );
  const currentSupplierProblems = useMemo(
    () =>
      getSupplierAnalysisProblems(
        {
          ...supplier,
          compliance_status: currentComplianceStatus,
        },
        documents,
      ),
    [currentComplianceStatus, documents, supplier],
  );
  const shouldShowProblemSummary =
    currentComplianceStatus !== 'Verified' &&
    currentComplianceStatus !== 'Unverified' &&
    currentSupplierProblems.length > 0;
  const activeRiskHelpContent =
    riskHelpTopic === null ? null : riskHelpContent[riskHelpTopic];
  const selectedRiskHelpCountryName = selectedRiskHelpCountry
    ? (getCountryNameFromIso(selectedRiskHelpCountry) ??
      selectedRiskHelpCountry)
    : '';
  const countryRiskSuggestionBadgeVariant = countryRiskSuggestion?.risk_level
    ? (riskBadgeVariantByLevel[
        countryRiskSuggestion.risk_level.trim().toLowerCase()
      ] ?? 'secondary')
    : 'secondary';
  const selectedRiskHelpIndustryLabel =
    industryOptions.find((option) => option.id === selectedRiskHelpIndustry)
      ?.label ?? selectedRiskHelpIndustry;
  const industryRiskSuggestionBadgeVariant = industryRiskSuggestion?.risk_level
    ? (riskBadgeVariantByLevel[
        industryRiskSuggestion.risk_level.trim().toLowerCase()
      ] ?? 'secondary')
    : 'secondary';

  const resetUploadForm = useCallback(() => {
    setUploadForm(emptyUploadForm);
    setSelectedFile(null);
    setFormErrors({});
    setUploadError(null);
    setFileInputKey((currentKey) => currentKey + 1);
  }, []);

  const resetRequestForm = useCallback(() => {
    setRequestForm({
      ...emptyRequestForm,
      email: supplier.email ?? '',
    });
    setRequestStep(1);
    setRequestFormErrors({});
    setRequestError(null);
    setExistingDocumentRequest(null);
  }, [supplier.email]);

  const reportUploadError = useCallback((message: string, error?: unknown) => {
    captureSupabaseError(error ?? new Error(message), {
      op: 'supply-chain.documents.upload',
    });
    setUploadError(message);
  }, []);

  const updateComplianceStatus = useCallback(
    async (
      nextDocuments: SupplyChainSupplierDocument[],
      nextCountryRisk: string | null,
      nextIndustryRisk: string | null,
    ) => {
      if (!organisationId) {
        throw new Error(
          'Organisation is required to update compliance status.',
        );
      }

      const nextComplianceStatus = calculateComplianceStatus(
        nextDocuments,
        nextCountryRisk,
        nextIndustryRisk,
      );

      const { error: statusUpdateError } = await supabase
        .from('supply_chain_suppliers')
        .update({
          compliance_status: nextComplianceStatus,
          updated_at: new Date().toISOString(),
        })
        .eq('id', supplierId)
        .eq('organisation_id', organisationId);

      if (statusUpdateError) {
        throw statusUpdateError;
      }
    },
    [organisationId, supplierId],
  );

  const fetchDocuments = useCallback(async () => {
    if (!supplierId) {
      setDocuments([]);
      return;
    }

    setLoading(true);
    setListError(null);

    const { data, error: fetchError } = await supabase
      .from('supply_chain_supplier_documents')
      .select('*')
      .eq('supplier_id', supplierId)
      .order('created_at', { ascending: false });

    if (fetchError) {
      captureSupabaseError(fetchError, { op: 'supply-chain.documents.fetch' });
      setListError(fetchError.message);
      setDocuments([]);
      setLoading(false);
      return;
    }

    const documentRows = (data ?? []) as SupplyChainSupplierDocument[];
    const enrichedDocuments = await Promise.all(
      documentRows.map(async (document) => {
        if (!document.file_url) {
          return document;
        }

        if (document.file_url.startsWith('http')) {
          return {
            ...document,
            signed_url: document.file_url,
          };
        }

        const { data: signedUrlData, error: signedUrlError } =
          await supabase.storage
            .from(bucketName)
            .createSignedUrl(document.file_url, 60 * 5);

        if (signedUrlError) {
          captureSupabaseError(signedUrlError, {
            op: 'supply-chain.documents.signed-url',
          });
        }

        return {
          ...document,
          signed_url: signedUrlData?.signedUrl ?? null,
        };
      }),
    );

    setDocuments(enrichedDocuments);
    setLoading(false);
  }, [bucketName, supplierId]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  useEffect(() => {
    setCountryRisk(supplier.country_risk ?? '');
    setIndustryRisk(supplier.industry_risk ?? '');
  }, [supplier.country_risk, supplier.industry_risk]);

  useEffect(() => {
    setRequestForm((currentForm) => ({
      ...currentForm,
      email: currentForm.email || supplier.email || '',
    }));
  }, [supplier.email]);

  useEffect(() => {
    setRequestForm((currentForm) => {
      if (availableSlots <= 0) {
        if (
          currentForm.items.length === 0 &&
          !currentForm.requestedDocumentName
        ) {
          return currentForm;
        }

        return {
          ...currentForm,
          requestedDocumentName: '',
          items: [],
        };
      }

      if (currentForm.items.length <= availableSlots) {
        return currentForm;
      }

      return {
        ...currentForm,
        items: currentForm.items.slice(0, availableSlots),
      };
    });
  }, [availableSlots]);

  useEffect(() => {
    if (riskHelpTopic !== 'country') {
      setSelectedRiskHelpCountry('');
      setCountryRiskSuggestion(null);
      setCountryRiskSuggestionError(null);
      setCountryRiskSuggestionLoading(false);
    }

    if (riskHelpTopic !== 'industry') {
      setSelectedRiskHelpIndustry('');
      setIndustryRiskSuggestion(null);
      setIndustryRiskSuggestionError(null);
      setIndustryRiskSuggestionLoading(false);
    }
  }, [riskHelpTopic]);

  useEffect(() => {
    if (riskHelpTopic !== 'industry') {
      return;
    }

    let isActive = true;

    const fetchIndustryOptions = async () => {
      setIndustryOptionsLoading(true);

      const { data, error } = await supabase
        .from('industry_options')
        .select('id, label')
        .order('label', { ascending: true });

      if (!isActive) {
        return;
      }

      if (error) {
        captureSupabaseError(error, {
          op: 'supply-chain.industry-options.fetch',
        });
        setIndustryOptions([]);
        setIndustryRiskSuggestionError(error.message);
        setIndustryOptionsLoading(false);
        return;
      }

      setIndustryOptions(
        ((data ?? []) as Array<{ id: string; label: string }>).map(
          (industry) => ({
            id: industry.id,
            label: industry.label,
          }),
        ),
      );
      setIndustryOptionsLoading(false);
    };

    fetchIndustryOptions();

    return () => {
      isActive = false;
    };
  }, [riskHelpTopic]);

  useEffect(() => {
    if (riskHelpTopic !== 'country' || !selectedRiskHelpCountry) {
      return;
    }

    let isActive = true;

    const fetchCountryRiskSuggestion = async () => {
      setCountryRiskSuggestionLoading(true);
      setCountryRiskSuggestionError(null);

      const { data, error } = await supabase
        .from('supply_chain_country_risk')
        .select(
          'country_code, risk_level, cpi_score, ituc_rating, suggestion_logic',
        )
        .eq('country_code', selectedRiskHelpCountry)
        .maybeSingle();

      if (!isActive) {
        return;
      }

      if (error) {
        captureSupabaseError(error, {
          op: 'supply-chain.country-risk.fetch',
          countryCode: selectedRiskHelpCountry,
        });
        setCountryRiskSuggestion(null);
        setCountryRiskSuggestionError(error.message);
        setCountryRiskSuggestionLoading(false);
        return;
      }

      if (!data) {
        setCountryRiskSuggestion(null);
        setCountryRiskSuggestionError(
          'No suggested risk level is available for this country.',
        );
        setCountryRiskSuggestionLoading(false);
        return;
      }

      setCountryRiskSuggestion(data as CountryRiskSuggestion);
      setCountryRiskSuggestionLoading(false);
    };

    fetchCountryRiskSuggestion();

    return () => {
      isActive = false;
    };
  }, [riskHelpTopic, selectedRiskHelpCountry]);

  useEffect(() => {
    if (riskHelpTopic !== 'industry' || !selectedRiskHelpIndustry) {
      return;
    }

    let isActive = true;

    const fetchIndustryRiskSuggestion = async () => {
      setIndustryRiskSuggestionLoading(true);
      setIndustryRiskSuggestionError(null);

      const { data, error } = await supabase
        .from('supply_chain_industry_risk')
        .select('industry_id, risk_level, risk_drivers')
        .eq('industry_id', selectedRiskHelpIndustry)
        .maybeSingle();

      if (!isActive) {
        return;
      }

      if (error) {
        captureSupabaseError(error, {
          op: 'supply-chain.industry-risk.fetch',
          industryId: selectedRiskHelpIndustry,
        });
        setIndustryRiskSuggestion(null);
        setIndustryRiskSuggestionError(error.message);
        setIndustryRiskSuggestionLoading(false);
        return;
      }

      if (!data) {
        setIndustryRiskSuggestion(null);
        setIndustryRiskSuggestionError(
          'No suggested risk level is available for this industry.',
        );
        setIndustryRiskSuggestionLoading(false);
        return;
      }

      setIndustryRiskSuggestion(data as IndustryRiskSuggestion);
      setIndustryRiskSuggestionLoading(false);
    };

    fetchIndustryRiskSuggestion();

    return () => {
      isActive = false;
    };
  }, [riskHelpTopic, selectedRiskHelpIndustry]);

  const fetchExistingDocumentRequest = useCallback(async () => {
    if (!supplierOrganisationId || !supplierId) {
      setExistingDocumentRequest(null);
      return;
    }

    setRequestLoading(true);
    setRequestError(null);

    const { data, error } = await supabase
      .from('supply_chain_document_requests')
      .select(
        'id, supplier_id, organisation_id, recipient_email, message, due_date, status, created_at, deleted_at',
      )
      .eq('supplier_id', supplierId)
      .eq('organisation_id', supplierOrganisationId)
      .is('deleted_at', null)
      .eq('status', 'pending')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      captureSupabaseError(error, {
        op: 'supply-chain.document-request.fetch',
      });
      setExistingDocumentRequest(null);
      setRequestError(error.message);
      setRequestLoading(false);
      return;
    }

    const requestRow = (data as SupplyChainDocumentRequest | null) ?? null;

    if (!requestRow) {
      setExistingDocumentRequest(null);
      setRequestLoading(false);
      return;
    }

    const { data: itemRows, error: itemError } = await supabase
      .from('supply_chain_document_request_items')
      .select('id, requested_document_name')
      .eq('request_id', requestRow.id)
      .order('requested_document_name', { ascending: true });

    if (itemError) {
      captureSupabaseError(itemError, {
        op: 'supply-chain.document-request-items.fetch',
        requestId: requestRow.id,
      });
      setExistingDocumentRequest(null);
      setRequestError(itemError.message);
      setRequestLoading(false);
      return;
    }

    setExistingDocumentRequest({
      ...requestRow,
      items: (itemRows as SupplyChainDocumentRequestItem[] | null) ?? [],
    });
    setRequestLoading(false);
  }, [supplierOrganisationId, supplierId]);

  const openUploadModal = () => {
    resetUploadForm();
    setShowUploadModal(true);
  };

  const requestDocuments = useCallback(() => {
    resetRequestForm();
    setRequestStep(1);
    setShowRequestModal(true);
    fetchExistingDocumentRequest();
  }, [fetchExistingDocumentRequest, resetRequestForm]);

  const closeUploadModal = useCallback(() => {
    setShowUploadModal(false);
    resetUploadForm();
  }, [resetUploadForm]);

  const closeRequestModal = useCallback(() => {
    setShowRequestModal(false);
    resetRequestForm();
    setRequestStep(1);
    setRequestLoading(false);
    setRequestSubmitting(false);
    setRequestCancelling(false);
  }, [resetRequestForm]);

  const sanitizeFileName = (fileName: string) =>
    fileName.replace(/[^a-zA-Z0-9._-]/g, '_');

  const handleUploadDocument = useCallback(async () => {
    const nextErrors: Record<string, string> = {};

    if (!selectedFile) {
      nextErrors.file = 'A file is required';
    }

    if (!uploadForm.fileName.trim()) {
      nextErrors.fileName = 'File name is required';
    }

    if (!uploadForm.validUntil) {
      nextErrors.validUntil = 'Valid until date is required';
    }

    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      return;
    }

    if (availableSlots <= 0) {
      reportUploadError(`You can only upload up to ${MAX_FILES} files.`);
      return;
    }

    if (!organisationId) {
      reportUploadError('Organisation is required to upload compliance files.');
      return;
    }

    if (!selectedFile) {
      return;
    }

    setSaving(true);
    setUploadError(null);

    try {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError) {
        throw userError;
      }

      const safeFileName = sanitizeFileName(selectedFile.name);
      const storagePath = `supplier-${supplierId}/compliance/${Date.now()}_${safeFileName}`;

      const { error: uploadError } = await supabase.storage
        .from(bucketName)
        .upload(storagePath, selectedFile, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) {
        throw uploadError;
      }

      const { error: insertError } = await supabase
        .from('supply_chain_supplier_documents')
        .insert({
          supplier_id: supplierId,
          organisation_id: organisationId,
          file_name: uploadForm.fileName.trim(),
          file_url: storagePath,
          valid_until: uploadForm.validUntil,
          uploaded_by: user?.id ?? null,
        });

      if (insertError) {
        throw insertError;
      }

      const nextDocuments: SupplyChainSupplierDocument[] = [
        {
          id: 'pending-upload',
          supplier_id: supplierId,
          organisation_id: organisationId,
          file_name: uploadForm.fileName.trim(),
          file_url: storagePath,
          valid_until: uploadForm.validUntil,
          created_at: new Date().toISOString(),
          uploaded_by: user?.id ?? null,
        },
        ...documents,
      ];

      await updateComplianceStatus(nextDocuments, countryRisk, industryRisk);
      closeUploadModal();
      await fetchDocuments();
      await onSupplierUpdated();
    } catch (uploadError) {
      reportUploadError(
        uploadError instanceof Error
          ? uploadError.message
          : 'Unable to upload compliance documents.',
        uploadError,
      );
    } finally {
      setSaving(false);
    }
  }, [
    organisationId,
    reportUploadError,
    selectedFile,
    supplierId,
    uploadForm.fileName,
    uploadForm.validUntil,
    availableSlots,
    bucketName,
    countryRisk,
    closeUploadModal,
    documents,
    fetchDocuments,
    industryRisk,
    onSupplierUpdated,
    updateComplianceStatus,
  ]);

  const handleDeleteDocument = async (
    document: SupplyChainSupplierDocument,
  ) => {
    if (!confirm('Delete this compliance file?')) {
      return;
    }

    setSaving(true);
    setListError(null);

    try {
      if (document.file_url && !document.file_url.startsWith('http')) {
        const { error: removeError } = await supabase.storage
          .from(bucketName)
          .remove([document.file_url]);

        if (removeError) {
          throw removeError;
        }
      }

      const { error: deleteError } = await supabase
        .from('supply_chain_supplier_documents')
        .delete()
        .eq('id', document.id);

      if (deleteError) {
        throw deleteError;
      }

      const nextDocuments = documents.filter(
        (existingDocument) => existingDocument.id !== document.id,
      );

      await updateComplianceStatus(nextDocuments, countryRisk, industryRisk);
      await fetchDocuments();
      await onSupplierUpdated();
    } catch (deleteError) {
      captureSupabaseError(deleteError, {
        op: 'supply-chain.documents.delete',
      });
      setListError(
        deleteError instanceof Error
          ? deleteError.message
          : 'Unable to delete compliance document.',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitDocumentRequest = useCallback(async () => {
    const nextErrors: Record<string, string> = {};

    if (!requestForm.email.trim()) {
      nextErrors.email = 'Email is required';
    }

    if (requestForm.items.length === 0) {
      nextErrors.items = 'Add at least one requested document';
    }

    if (Object.keys(nextErrors).length > 0) {
      setRequestFormErrors(nextErrors);
      return;
    }

    setRequestSubmitting(true);
    setRequestError(null);
    setRequestFormErrors({});

    try {
      const { error } = await supabase.functions.invoke(
        'supply-chain-document-request',
        {
          body: {
            supplierId,
            email: requestForm.email.trim(),
            message: requestForm.message.trim(),
            dueDate: requestForm.dueDate || null,
            organisationId: supplierOrganisationId,
            documentsCount: requestForm.items.length,
            items: requestForm.items.map((item) => ({
              requested_document_name: item.requested_document_name,
            })),
          },
        },
      );

      if (error) {
        throw error;
      }

      closeRequestModal();
    } catch (submitError) {
      captureSupabaseError(submitError, {
        op: 'supply-chain.document-request.create',
      });
      setRequestError(
        submitError instanceof Error
          ? submitError.message
          : 'Unable to request documents.',
      );
    } finally {
      setRequestSubmitting(false);
    }
  }, [
    closeRequestModal,
    requestForm.dueDate,
    requestForm.email,
    requestForm.items,
    requestForm.message,
    supplierOrganisationId,
    supplierId,
  ]);

  const handleAddRequestedDocumentItem = useCallback(() => {
    const nextName = requestForm.requestedDocumentName.trim();

    if (!nextName) {
      setRequestFormErrors((currentErrors) => ({
        ...currentErrors,
        requestedDocumentName: 'Requested document name is required',
      }));
      return;
    }

    if (requestForm.items.length >= availableSlots) {
      setRequestFormErrors((currentErrors) => ({
        ...currentErrors,
        items: `You can request up to ${availableSlots} documents.`,
      }));
      return;
    }

    setRequestForm((currentForm) => ({
      ...currentForm,
      requestedDocumentName: '',
      items: [...currentForm.items, { requested_document_name: nextName }],
    }));
    setRequestFormErrors((currentErrors) => {
      const nextErrors = { ...currentErrors };
      delete nextErrors.requestedDocumentName;
      delete nextErrors.items;
      return nextErrors;
    });
  }, [
    availableSlots,
    requestForm.items.length,
    requestForm.requestedDocumentName,
  ]);

  const handleRemoveRequestedDocumentItem = useCallback((itemIndex: number) => {
    setRequestForm((currentForm) => ({
      ...currentForm,
      items: currentForm.items.filter((_, index) => index !== itemIndex),
    }));
    setRequestFormErrors((currentErrors) => {
      const nextErrors = { ...currentErrors };
      delete nextErrors.items;
      return nextErrors;
    });
  }, []);

  const handleGoToRequestStep = useCallback(
    (nextStep: 1 | 2) => {
      if (nextStep === 2 && requestForm.items.length === 0) {
        setRequestFormErrors((currentErrors) => ({
          ...currentErrors,
          items: 'Add at least one requested document',
        }));
        return;
      }

      setRequestStep(nextStep);
    },
    [requestForm.items.length],
  );

  const handleCancelDocumentRequest = useCallback(async () => {
    if (!existingDocumentRequest) {
      return;
    }

    if (!confirm('Cancel the existing document request?')) {
      return;
    }

    setRequestCancelling(true);
    setRequestError(null);

    try {
      const { error } = await supabase
        .from('supply_chain_document_requests')
        .update({
          deleted_at: new Date().toISOString(),
        })
        .eq('id', existingDocumentRequest.id);

      if (error) {
        throw error;
      }

      setExistingDocumentRequest(null);
      setRequestForm({
        email: existingDocumentRequest.recipient_email || supplier.email || '',
        message: existingDocumentRequest.message || '',
        dueDate: existingDocumentRequest.due_date || '',
        requestedDocumentName: '',
        items:
          existingDocumentRequest.items
            ?.filter((item) => item.requested_document_name?.trim())
            .slice(0, availableSlots) ?? [],
      });
    } catch (cancelError) {
      captureSupabaseError(cancelError, {
        op: 'supply-chain.document-request.cancel',
        requestId: existingDocumentRequest.id,
      });
      setRequestError(
        cancelError instanceof Error
          ? cancelError.message
          : 'Unable to cancel document request.',
      );
    } finally {
      setRequestCancelling(false);
    }
  }, [availableSlots, existingDocumentRequest, supplier.email]);

  const handleRiskChange = useCallback(
    async (field: 'country_risk' | 'industry_risk', value: string) => {
      if (!organisationId) {
        setListError('Organisation is required to update supplier risks.');
        return;
      }

      if (field === 'country_risk') {
        setCountryRisk(value);
      } else {
        setIndustryRisk(value);
      }

      setSavingRiskField(field);
      setListError(null);

      try {
        const nextCountryRisk =
          field === 'country_risk' ? value : countryRisk || null;
        const nextIndustryRisk =
          field === 'industry_risk' ? value : industryRisk || null;
        const nextComplianceStatus = calculateComplianceStatus(
          documents,
          nextCountryRisk,
          nextIndustryRisk,
        );

        const { error: updateError } = await supabase
          .from('supply_chain_suppliers')
          .update({
            [field]: value,
            compliance_status: nextComplianceStatus,
            updated_at: new Date().toISOString(),
          })
          .eq('id', supplierId)
          .eq('organisation_id', organisationId);

        if (updateError) {
          throw updateError;
        }

        await onSupplierUpdated();
      } catch (updateError) {
        captureSupabaseError(updateError, { op: 'supply-chain.risks.update' });
        setListError(
          updateError instanceof Error
            ? updateError.message
            : 'Unable to update supplier risks.',
        );

        if (field === 'country_risk') {
          setCountryRisk(supplier.country_risk ?? '');
        } else {
          setIndustryRisk(supplier.industry_risk ?? '');
        }
      } finally {
        setSavingRiskField(null);
      }
    },
    [
      countryRisk,
      documents,
      industryRisk,
      onSupplierUpdated,
      organisationId,
      supplier.country_risk,
      supplier.industry_risk,
      supplierId,
    ],
  );

  return (
    <div className="pt-3 d-flex flex-column gap-3">
      {shouldShowProblemSummary && (
        <Alert variant="warning" className="mb-0 py-2">
          <ul className="mb-0 ps-3">
            {currentSupplierProblems.map((problem) => (
              <li key={problem.key}>{problem.label}</li>
            ))}
          </ul>
        </Alert>
      )}
      <div className="row g-3">
        <div className="col-sm-6">
          <Form.Group className="mb-0" controlId="countryRiskLevel">
            <Form.Label className="d-flex align-items-center gap-2">
              <span>Country risk level</span>
              <Button
                type="button"
                variant="link"
                className="p-0 text-decoration-none"
                onClick={() => setRiskHelpTopic('country')}
                aria-label="Explain country risk assessment"
              >
                <i className="bi bi-question-circle"></i>
              </Button>
            </Form.Label>
            <Form.Select
              value={countryRisk}
              onChange={(event) =>
                handleRiskChange('country_risk', event.target.value)
              }
              disabled={savingRiskField === 'country_risk'}
            >
              <option value="">Select risk level</option>
              {riskOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>
        <div className="col-sm-6">
          <Form.Group className="mb-0" controlId="industryRiskLevel">
            <Form.Label className="d-flex align-items-center gap-2">
              <span>Industry risk level</span>
              <Button
                type="button"
                variant="link"
                className="p-0 text-decoration-none"
                onClick={() => setRiskHelpTopic('industry')}
                aria-label="Explain industry risk assessment"
              >
                <i className="bi bi-question-circle"></i>
              </Button>
            </Form.Label>
            <Form.Select
              value={industryRisk}
              onChange={(event) =>
                handleRiskChange('industry_risk', event.target.value)
              }
              disabled={savingRiskField === 'industry_risk'}
            >
              <option value="">Select risk level</option>
              {riskOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>
      </div>

      <div className="d-flex align-items-center justify-content-between">
        <div>
          <h6 className="mb-1">Compliance files</h6>
          <div className="small text-muted">
            Add up to {MAX_FILES} files for this supplier.
          </div>
        </div>
        <DropdownButton
          id="dropdown-basic-button"
          disabled={saving || availableSlots <= 0}
          title="Add documents"
        >
          <Dropdown.Item onClick={openUploadModal}>
            <i className="bi bi-cloud-plus me-2"></i> Upload documents
          </Dropdown.Item>
          <Dropdown.Item onClick={requestDocuments}>
            <i className="bi bi-envelope me-2"></i> Request documents
          </Dropdown.Item>
        </DropdownButton>
        {/* <Button
          variant="outline-primary"
          size="sm"
          onClick={openUploadModal}
          disabled={saving || availableSlots <= 0}
        >
          <i className="bi bi-plus-lg"></i>
        </Button> */}
      </div>

      {documents.length >= MAX_FILES && (
        <Alert variant="info" className="mb-0">
          You have reached the maximum of {MAX_FILES} compliance files.
        </Alert>
      )}

      {/* <div>
        <Button variant="outline-primary">Request documents</Button>
      </div> */}

      {listError && (
        <Alert variant="danger" className="mb-0">
          {listError}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" />
        </div>
      ) : documents.length ? (
        <Table responsive size="sm" className="mb-0 align-middle">
          <thead>
            <tr>
              <th>File</th>
              <th>Valid until</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {documents.map((document) => (
              <tr key={document.id}>
                <td>
                  {document.signed_url ? (
                    <a
                      href={document.signed_url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {document.file_name}
                    </a>
                  ) : (
                    document.file_name
                  )}
                </td>
                <td>
                  <span
                    className={
                      document.valid_until && isDocumentExpired(document)
                        ? 'fw-semibold text-warning'
                        : undefined
                    }
                  >
                    {document.valid_until
                      ? new Date(document.valid_until).toLocaleDateString()
                      : '—'}
                  </span>
                </td>
                <td>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => handleDeleteDocument(document)}
                    disabled={saving}
                  >
                    <i className="bi bi-trash"></i>
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <p className="text-muted mb-0">No compliance files uploaded yet.</p>
      )}

      <Modal show={showUploadModal} onHide={closeUploadModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Upload compliance file</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {uploadError && (
            <Alert variant="danger" className="mb-3">
              {uploadError}
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label>File *</Form.Label>
            <Form.Control
              key={fileInputKey}
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
              isInvalid={Boolean(formErrors.file)}
              onChange={(event) => {
                const input = event.target as HTMLInputElement;
                setSelectedFile(input.files?.[0] ?? null);
              }}
              disabled={saving}
            />
            <Form.Control.Feedback type="invalid">
              {formErrors.file}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>File name *</Form.Label>
            <Form.Control
              type="text"
              value={uploadForm.fileName}
              isInvalid={Boolean(formErrors.fileName)}
              onChange={(event) =>
                setUploadForm((currentForm) => ({
                  ...currentForm,
                  fileName: event.target.value,
                }))
              }
              disabled={saving}
            />
            <Form.Control.Feedback type="invalid">
              {formErrors.fileName}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group>
            <Form.Label>Valid until *</Form.Label>
            <Form.Control
              type="date"
              value={uploadForm.validUntil}
              isInvalid={Boolean(formErrors.validUntil)}
              onChange={(event) =>
                setUploadForm((currentForm) => ({
                  ...currentForm,
                  validUntil: event.target.value,
                }))
              }
              disabled={saving}
            />
            <Form.Control.Feedback type="invalid">
              {formErrors.validUntil}
            </Form.Control.Feedback>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeUploadModal}>
            Cancel
          </Button>
          <Button onClick={handleUploadDocument} disabled={saving}>
            {saving ? 'Uploading...' : 'Upload file'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showRequestModal}
        onHide={closeRequestModal}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Request documents</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {requestError && (
            <Alert variant="danger" className="mb-3">
              {requestError}
            </Alert>
          )}

          {requestLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : existingDocumentRequest ? (
            <div className="d-flex flex-column gap-3">
              <Alert variant="info" className="mb-0">
                There is already an existing request. You can cancel the
                existing request to send a new one.
              </Alert>
              <div className="rounded border bg-light p-3">
                <p className="mb-2">
                  <strong>Recipient:</strong>{' '}
                  {existingDocumentRequest.recipient_email || '—'}
                </p>
                <div className="mb-2">
                  <strong>Requested items:</strong>
                  {existingDocumentRequest.items?.length ? (
                    <ul className="mb-0 mt-2 ps-3">
                      {existingDocumentRequest.items.map((item, index) => (
                        <li key={`${item.requested_document_name}-${index}`}>
                          {item.requested_document_name}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span> —</span>
                  )}
                </div>
                <p className="mb-2">
                  <strong>Message:</strong>{' '}
                  {existingDocumentRequest.message || '—'}
                </p>
                <p className="mb-2">
                  <strong>Due date:</strong>{' '}
                  {existingDocumentRequest.due_date
                    ? new Date(
                        `${existingDocumentRequest.due_date}T00:00:00`,
                      ).toLocaleDateString()
                    : '—'}
                </p>
                <p className="mb-0">
                  <strong>Created at:</strong>{' '}
                  {existingDocumentRequest.created_at
                    ? new Date(
                        existingDocumentRequest.created_at,
                      ).toLocaleString()
                    : '—'}
                </p>
              </div>
            </div>
          ) : (
            <>
              {availableSlots <= 0 ? (
                <Alert variant="warning" className="mb-0">
                  The maximum number of documents has been reached. Delete one
                  or more existing documents before requesting new ones.
                </Alert>
              ) : (
                <>
                  <p className="fw-bold text-muted">
                    You can request compliance documents from the supplier by
                    filling out the form below. The supplier will receive an
                    email with your request and can respond by uploading the
                    requested documents.
                  </p>
                  {requestStep === 1 ? (
                    <div className="rounded border p-3 p-md-4 bg-white">
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <div
                          className="d-inline-flex align-items-center justify-content-center rounded-circle text-white fw-semibold"
                          style={{
                            width: '2rem',
                            height: '2rem',
                            background: '#1c1917',
                          }}
                        >
                          1
                        </div>
                        <div>
                          <div className="fw-semibold">
                            Step 1 - Requested documents
                          </div>
                          <div className="small text-muted">
                            Choose which documents the supplier needs to upload.
                            These will appear in the request they receive.
                          </div>
                        </div>
                      </div>

                      {/* <div className="rounded border bg-light-subtle p-3 p-md-4"> */}
                      {/* <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap mb-3">
                          <div>
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <div
                                className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white border"
                                style={{ width: '2rem', height: '2rem' }}
                              >
                                <i className="bi bi-card-checklist text-dark"></i>
                              </div>
                              <div className="fw-semibold fs-6">
                                Requested documents
                              </div>
                            </div>
                            <div className="small text-muted">
                              Add the names of the documents the supplier needs
                              to upload. These will appear in the request they
                              receive.
                            </div>
                          </div>
                          <div className="small fw-semibold text-muted">
                            {requestForm.items.length} / {availableSlots} added
                          </div>
                        </div> */}
                      <div className="small fw-semibold text-muted">
                        {requestForm.items.length} / {availableSlots} added
                      </div>

                      {requestForm.items.length ? (
                        <div className="d-flex flex-column gap-2 mb-3">
                          {requestForm.items.map((item, index) => (
                            <div
                              key={`${item.requested_document_name}-${index}`}
                              className="d-flex align-items-center justify-content-between gap-3 rounded border bg-white px-3 py-2 shadow-sm"
                            >
                              <div className="d-flex align-items-center gap-2">
                                <i className="bi bi-file-earmark-text text-muted"></i>
                                <span>{item.requested_document_name}</span>
                              </div>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() =>
                                  handleRemoveRequestedDocumentItem(index)
                                }
                                disabled={requestSubmitting}
                                aria-label={`Remove ${item.requested_document_name}`}
                              >
                                <i className="bi bi-x-lg"></i>
                              </Button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="rounded border bg-white px-3 py-4 text-center mb-3">
                          <div className="fw-semibold mb-1">
                            No requested documents yet
                          </div>
                        </div>
                        // <div className="rounded border bg-white px-3 py-4 text-center mb-3">
                        //   <div className="text-muted mb-2">
                        //     <i className="bi bi-file-earmark-plus fs-4"></i>
                        //   </div>
                        //   <div className="fw-semibold mb-1">
                        //     No requested documents yet
                        //   </div>
                        //   <div className="small text-muted">
                        //     Start by adding each document name below, for
                        //     example an ISO certificate, audit report, or policy
                        //     document.
                        //   </div>
                        // </div>
                      )}

                      {requestFormErrors.items ? (
                        <div className="text-danger small mb-3">
                          {requestFormErrors.items}
                        </div>
                      ) : null}

                      <div className="rounded border bg-white p-3">
                        {/* <div className="small fw-semibold text-muted mb-2">
                            Add a requested document
                          </div> */}
                        <div className="row g-2 align-items-end">
                          <div className="col-md-9">
                            <Form.Group className="mb-0">
                              <Form.Label>Requested document</Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="e.g. ISO 14001 certificate"
                                value={requestForm.requestedDocumentName}
                                isInvalid={Boolean(
                                  requestFormErrors.requestedDocumentName,
                                )}
                                onChange={(event) =>
                                  setRequestForm((currentForm) => ({
                                    ...currentForm,
                                    requestedDocumentName: event.target.value,
                                  }))
                                }
                                disabled={
                                  requestSubmitting ||
                                  requestForm.items.length >= availableSlots
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {requestFormErrors.requestedDocumentName}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </div>
                          <div className="col-md-3 d-grid">
                            <Button
                              type="button"
                              size="sm"
                              onClick={handleAddRequestedDocumentItem}
                              disabled={
                                requestSubmitting ||
                                requestForm.items.length >= availableSlots
                              }
                            >
                              <i className="bi bi-plus-lg me-1"></i>
                              Add
                            </Button>
                          </div>
                        </div>
                      </div>
                      {/* </div> */}
                    </div>
                  ) : (
                    <div className="rounded border p-3 p-md-4 bg-white">
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <div
                          className="d-inline-flex align-items-center justify-content-center rounded-circle text-white fw-semibold"
                          style={{
                            width: '2rem',
                            height: '2rem',
                            background: '#1c1917',
                          }}
                        >
                          2
                        </div>
                        <div>
                          <div className="fw-semibold">Step 2</div>
                          <div className="small text-muted">
                            Enter where to send the request and any optional
                            note.
                          </div>
                        </div>
                      </div>

                      <div className="row g-3 mb-3">
                        <div className="col-md-8">
                          <Form.Group className="mb-0">
                            <Form.Label>Email *</Form.Label>
                            <Form.Control
                              type="email"
                              value={requestForm.email}
                              isInvalid={Boolean(requestFormErrors.email)}
                              onChange={(event) =>
                                setRequestForm((currentForm) => ({
                                  ...currentForm,
                                  email: event.target.value,
                                }))
                              }
                              disabled={requestSubmitting}
                            />
                            <Form.Control.Feedback type="invalid">
                              {requestFormErrors.email}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </div>
                        <div className="col-md-4">
                          <Form.Group className="mb-0">
                            <Form.Label>Due date (optional)</Form.Label>
                            <Form.Control
                              type="date"
                              value={requestForm.dueDate}
                              onChange={(event) =>
                                setRequestForm((currentForm) => ({
                                  ...currentForm,
                                  dueDate: event.target.value,
                                }))
                              }
                              disabled={requestSubmitting}
                            />
                          </Form.Group>
                        </div>
                      </div>

                      <Form.Group>
                        <Form.Label>Message</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={requestForm.message}
                          onChange={(event) =>
                            setRequestForm((currentForm) => ({
                              ...currentForm,
                              message: event.target.value,
                            }))
                          }
                          disabled={requestSubmitting}
                        />
                      </Form.Group>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeRequestModal}>
            {existingDocumentRequest ? 'Close' : 'Cancel'}
          </Button>
          {existingDocumentRequest ? (
            <Button
              variant="outline-danger"
              onClick={handleCancelDocumentRequest}
              disabled={requestCancelling}
            >
              {requestCancelling ? 'Cancelling...' : 'Cancel request'}
            </Button>
          ) : requestStep === 1 ? (
            <Button
              onClick={() => handleGoToRequestStep(2)}
              disabled={requestSubmitting || availableSlots <= 0}
            >
              Next
            </Button>
          ) : (
            <>
              <Button
                variant="outline-secondary"
                onClick={() => handleGoToRequestStep(1)}
                disabled={requestSubmitting}
              >
                Back
              </Button>
              <Button
                onClick={handleSubmitDocumentRequest}
                disabled={requestSubmitting || availableSlots <= 0}
              >
                {requestSubmitting ? 'Sending...' : 'Request documents'}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      <Modal
        show={riskHelpTopic !== null}
        onHide={() => setRiskHelpTopic(null)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{activeRiskHelpContent?.heading}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {activeRiskHelpContent ? (
            <div className="d-flex flex-column gap-3">
              <p className="mb-0">{activeRiskHelpContent.body}</p>
              {'bullets' in activeRiskHelpContent &&
              activeRiskHelpContent.bullets ? (
                <ul className="mb-0 ps-3">
                  {activeRiskHelpContent.bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
              ) : null}

              {riskHelpTopic === 'country' ? (
                <div className="mt-4 border-top pt-4">
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold text-muted">
                      Select a country to view suggested risk level
                    </Form.Label>
                    <CountrySelect
                      value={selectedRiskHelpCountry}
                      full
                      onChange={(value) => {
                        const nextCountry =
                          typeof value === 'string' ? value : '';
                        setSelectedRiskHelpCountry(nextCountry);
                        setCountryRiskSuggestion(null);
                        setCountryRiskSuggestionError(null);
                      }}
                    />
                  </Form.Group>

                  {countryRiskSuggestionLoading ? (
                    <div className="d-flex align-items-center gap-2 text-muted">
                      <Spinner animation="border" size="sm" />
                      <span>Loading suggested risk level...</span>
                    </div>
                  ) : null}

                  {!countryRiskSuggestionLoading &&
                  countryRiskSuggestionError ? (
                    <Alert variant="warning" className="mb-0">
                      {countryRiskSuggestionError}
                    </Alert>
                  ) : null}

                  {!countryRiskSuggestionLoading &&
                  !countryRiskSuggestionError &&
                  countryRiskSuggestion ? (
                    <div className="rounded border bg-light p-3">
                      <p className="mb-2">
                        <strong>
                          ESG Intelligence Engine {selectedRiskHelpCountryName}{' '}
                          risk assessment:
                        </strong>
                      </p>
                      <p className="mb-1">
                        <span className="fw-bold text-muted">
                          Governance (CPI):{' '}
                        </span>
                        {countryRiskSuggestion.cpi_score ?? 'N/A'}/100
                      </p>
                      <p className="mb-1">
                        <span className="fw-bold text-muted">
                          Labor (ITUC):{' '}
                        </span>
                        {countryRiskSuggestion.ituc_rating ?? 'N/A'}/5
                      </p>
                      <p className="mb-1">
                        <span className="fw-bold text-muted">Analysis: </span>
                        {countryRiskSuggestion.suggestion_logic ?? 'N/A'}
                      </p>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <span className="fw-bold text-muted">
                          Suggested risk level:
                        </span>
                        <Badge bg={countryRiskSuggestionBadgeVariant}>
                          {countryRiskSuggestion.risk_level}
                        </Badge>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {riskHelpTopic === 'industry' ? (
                <div className="mt-4 border-top pt-4">
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold text-muted">
                      Select an industry to view suggested risk level
                    </Form.Label>
                    {industryOptionsLoading ? (
                      <div className="d-flex align-items-center gap-2 text-muted">
                        <Spinner animation="border" size="sm" />
                        <span>Loading industries...</span>
                      </div>
                    ) : (
                      <DropdownSelect
                        options={industryOptions}
                        selectedIds={selectedRiskHelpIndustry}
                        placeholder="Select"
                        full
                        onValueChanged={(selected) => {
                          const option = selected as DropdownSelectOption;
                          setSelectedRiskHelpIndustry(String(option.id));
                          setIndustryRiskSuggestion(null);
                          setIndustryRiskSuggestionError(null);
                        }}
                      />
                    )}
                  </Form.Group>

                  {!industryOptionsLoading && industryRiskSuggestionLoading ? (
                    <div className="d-flex align-items-center gap-2 text-muted">
                      <Spinner animation="border" size="sm" />
                      <span>Loading suggested risk level...</span>
                    </div>
                  ) : null}

                  {!industryOptionsLoading &&
                  !industryRiskSuggestionLoading &&
                  industryRiskSuggestionError ? (
                    <Alert variant="warning" className="mb-0">
                      {industryRiskSuggestionError}
                    </Alert>
                  ) : null}

                  {!industryOptionsLoading &&
                  !industryRiskSuggestionLoading &&
                  !industryRiskSuggestionError &&
                  industryRiskSuggestion ? (
                    <div className="rounded border bg-light p-3">
                      <p className="mb-2">
                        <strong>
                          ESG Intelligence Engine{' '}
                          {selectedRiskHelpIndustryLabel} risk assessment:
                        </strong>
                      </p>
                      <p className="mb-1">
                        Analysis: {industryRiskSuggestion.risk_drivers ?? 'N/A'}
                      </p>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <span>Suggested risk level:</span>
                        <Badge bg={industryRiskSuggestionBadgeVariant}>
                          {industryRiskSuggestion.risk_level}
                        </Badge>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </Modal.Body>
      </Modal>
    </div>
  );
}
